import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { AdminService } from 'services/admin/admin';
import { RootState } from 'store/store';
import { ApiStatus } from 'types/enums/ApiStatus';

type CompanyNotCalledState = {
  status: ApiStatus.NOT_CALLED;
  errorCode?: never;
  organization?: never;
};

type CompanyLoadedState = {
  status: Exclude<ApiStatus, ApiStatus.NOT_CALLED>,
  errorCode?: number;
  organization: Organization;
};

export type CompanyState = CompanyNotCalledState | CompanyLoadedState;

const initialState: CompanyState = {
  status: ApiStatus.NOT_CALLED,
};

export const fetchOrganization = createAsyncThunk(
  'company/fetchOrganization',
  async (userId: number, { rejectWithValue }) => {
    try {
      const orgUserRole = await AdminService.Company.GetOrganizationUserRoles(userId);
      if (!orgUserRole || !orgUserRole.organizationId) {
        return rejectWithValue('No organization ID found');
      }

      const organization = await AdminService.Company.GetOrganization(orgUserRole.organizationId);
      return organization;
    } catch (error: unknown) {
      const axiosError = error as AxiosError;
      return rejectWithValue(axiosError.response?.status);
    }
  },
);

const companySlice = createSlice({
  name: 'company',
  initialState: initialState as CompanyState,
  reducers: {
    setOrganizationLogo: (
      state,
      { payload }: PayloadAction<string>,
    ) => {
      if (state.organization) {
        state.organization.logoUrl = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.status = ApiStatus.LOADING;
        state.errorCode = undefined;
      })
      .addCase(fetchOrganization.fulfilled, (state, { payload }) => {
        if (!payload) return;

        state.status = ApiStatus.SUCCEEDED;
        state.organization = payload;
      })
      .addCase(fetchOrganization.rejected, (state, { payload }) => {
        state.status = ApiStatus.FAILED;
        state.errorCode = payload as number;
      });
  },
});

export const { setOrganizationLogo } = companySlice.actions;

export default companySlice.reducer;

export const selectOrganizationUserRole = (state: RootState) =>
  state.company.organization;
